"use client";

import React, { useEffect, useRef, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import type { CategorySelection } from "@/lib/types";
import type { CategoryStructure } from "@/lib/categoryStructure";
import MarketCategoryDropdown from "./MarketCategoryDropdown";
import { MARKET_COPY } from "@/lib/market/copy";
import { resolveNextCategorySelection } from "@/lib/market/browseState";

interface MarketCategorySelectorProps {
  /** Top-level category chips — always shown; selected stays highlighted. */
  quickPicks: CategorySelection[];
  /** Subcategory refine chips for the selected category (empty otherwise). */
  subcategoryPicks: CategorySelection[];
  selectedCategory: CategorySelection | null;
  categoryStructure: CategoryStructure[];
  onCategorySelect: (category: CategorySelection | null) => void;
}

/**
 * The market's single category surface. Presentation-only: picks come from the
 * browse hook; selection flows through `onCategorySelect` (toggle semantics
 * live in `resolveNextCategorySelection` / `useMarketBrowse`).
 *
 * Nested rail (Phase 6.2):
 * - Category chips always stay visible (selected highlighted; re-tap clears).
 * - Subcategory chips appear below when a category is active.
 * - Chip toggle semantics live in `resolveNextCategorySelection`; the hook
 *   applies the resolved selection literally (dropdown stays non-toggling).
 * - "More" opens the full searchable list without replacing the chip rail.
 */
const MarketCategorySelector = React.memo(
  ({
    quickPicks,
    subcategoryPicks,
    selectedCategory,
    categoryStructure,
    onCategorySelect,
  }: MarketCategorySelectorProps) => {
    const [showFullList, setShowFullList] = useState(false);
    const [categoryOverflow, setCategoryOverflow] = useState(false);
    const [subcategoryOverflow, setSubcategoryOverflow] = useState(false);

    const categoryRailRef = useRef<HTMLDivElement>(null);
    const subcategoryRailRef = useRef<HTMLDivElement>(null);
    const selectedCategoryChipRef = useRef<HTMLButtonElement>(null);
    const selectedSubcategoryChipRef = useRef<HTMLButtonElement>(null);

    const isCategoryActive = Boolean(selectedCategory);
    const showSubcategoryRail = isCategoryActive && subcategoryPicks.length > 0;

    const applyChipSelection = (pick: CategorySelection | null) => {
      onCategorySelect(resolveNextCategorySelection(selectedCategory, pick));
    };

    // Nothing to offer and no way to open the list — render nothing.
    if (quickPicks.length === 0 && categoryStructure.length === 0) return null;

    const isCategoryChipActive = (pick: CategorySelection) =>
      selectedCategory?.categoryId === pick.categoryId;

    const isSubcategoryActive = (pick: CategorySelection) =>
      Boolean(selectedCategory?.subcategoryId) &&
      selectedCategory?.subcategoryId === pick.subcategoryId;

    return (
      <section
        aria-label={MARKET_COPY.categoryQuickPicksHeading}
        className="mb-6 space-y-4"
      >
        <CategoryChipRail
          heading={MARKET_COPY.categoryQuickPicksHeading}
          picks={quickPicks}
          showAllChip={isCategoryActive}
          isChipActive={isCategoryChipActive}
          labelFor={(pick) => pick.categoryName}
          onSelect={applyChipSelection}
          onClear={() => onCategorySelect(null)}
          railRef={categoryRailRef}
          selectedChipRef={selectedCategoryChipRef}
          selectedKey={selectedCategory?.categoryId ?? null}
          hasOverflow={categoryOverflow}
          onOverflowChange={setCategoryOverflow}
          showFullList={showFullList}
          onToggleFullList={() => setShowFullList((open) => !open)}
          showMoreButton={categoryStructure.length > 0}
        />

        {showSubcategoryRail && (
          <SubcategoryChipRail
            picks={subcategoryPicks}
            isChipActive={isSubcategoryActive}
            onSelect={applyChipSelection}
            railRef={subcategoryRailRef}
            selectedChipRef={selectedSubcategoryChipRef}
            hasOverflow={subcategoryOverflow}
            onOverflowChange={setSubcategoryOverflow}
          />
        )}

        {showFullList && (
          <div className="mt-3 max-w-xs">
            <MarketCategoryDropdown
              selectedCategory={selectedCategory}
              onCategorySelect={(category) => {
                onCategorySelect(category);
                setShowFullList(false);
              }}
              categoryStructure={categoryStructure}
            />
          </div>
        )}
      </section>
    );
  },
);
MarketCategorySelector.displayName = "MarketCategorySelector";

interface CategoryChipRailProps {
  heading: string;
  picks: CategorySelection[];
  showAllChip: boolean;
  isChipActive: (pick: CategorySelection) => boolean;
  labelFor: (pick: CategorySelection) => string | undefined;
  onSelect: (category: CategorySelection | null) => void;
  onClear: () => void;
  railRef: React.RefObject<HTMLDivElement | null>;
  selectedChipRef: React.RefObject<HTMLButtonElement | null>;
  selectedKey: string | null;
  hasOverflow: boolean;
  onOverflowChange: (overflow: boolean) => void;
  showFullList: boolean;
  onToggleFullList: () => void;
  showMoreButton: boolean;
}

function CategoryChipRail({
  heading,
  picks,
  showAllChip,
  isChipActive,
  labelFor,
  onSelect,
  onClear,
  railRef,
  selectedChipRef,
  selectedKey,
  hasOverflow,
  onOverflowChange,
  showFullList,
  onToggleFullList,
  showMoreButton,
}: CategoryChipRailProps) {
  useChipRailOverflow(railRef, onOverflowChange);
  useScrollSelectedChipIntoView(railRef, selectedChipRef, selectedKey);

  return (
    <div>
      <h2 className="text-sm font-medium text-text-light mb-3">{heading}</h2>
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <div
            ref={railRef}
            className="flex overflow-x-auto scrollbar-hide items-center gap-2 pb-1 -mx-1 px-1"
          >
            {showAllChip && (
              <button
                type="button"
                onClick={onClear}
                aria-pressed={false}
                className="flex-shrink-0 rounded-full text-sm px-4 py-1.5 transition-colors whitespace-nowrap border border-border text-foreground hover:bg-secondary"
              >
                {MARKET_COPY.categoryAllLabel}
              </button>
            )}
            {picks.map((pick) => {
              const active = isChipActive(pick);
              return (
                <button
                  key={pick.categoryId}
                  ref={active ? selectedChipRef : undefined}
                  type="button"
                  onClick={() => onSelect(pick)}
                  aria-pressed={active}
                  className={`flex-shrink-0 rounded-full text-sm px-4 py-1.5 transition-colors whitespace-nowrap ${
                    active
                      ? "bg-primary text-white"
                      : "bg-secondary text-foreground hover:bg-tertiary"
                  }`}
                >
                  {labelFor(pick)}
                </button>
              );
            })}
          </div>
          {hasOverflow && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent"
            />
          )}
        </div>

        {showMoreButton && (
          <button
            type="button"
            onClick={onToggleFullList}
            aria-expanded={showFullList}
            className="flex-shrink-0 inline-flex items-center gap-1 rounded-full border border-border text-sm text-foreground px-4 py-1.5 hover:bg-secondary transition-colors whitespace-nowrap"
          >
            {showFullList
              ? MARKET_COPY.categoryMoreCloseLabel
              : MARKET_COPY.categoryMoreLabel}
            {showFullList ? (
              <ChevronUpIcon className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}

interface SubcategoryChipRailProps {
  picks: CategorySelection[];
  isChipActive: (pick: CategorySelection) => boolean;
  onSelect: (category: CategorySelection) => void;
  railRef: React.RefObject<HTMLDivElement | null>;
  selectedChipRef: React.RefObject<HTMLButtonElement | null>;
  hasOverflow: boolean;
  onOverflowChange: (overflow: boolean) => void;
}

function SubcategoryChipRail({
  picks,
  isChipActive,
  onSelect,
  railRef,
  selectedChipRef,
  hasOverflow,
  onOverflowChange,
}: SubcategoryChipRailProps) {
  const activeSubId = picks.find((p) => isChipActive(p))?.subcategoryId ?? null;
  useChipRailOverflow(railRef, onOverflowChange);
  useScrollSelectedChipIntoView(railRef, selectedChipRef, activeSubId);

  return (
    <div>
      <h3 className="text-sm font-medium text-text-light mb-3">
        {MARKET_COPY.categoryRefineHeading}
      </h3>
      <div className="relative min-w-0">
        <div
          ref={railRef}
          className="flex overflow-x-auto scrollbar-hide items-center gap-2 pb-1 -mx-1 px-1"
        >
          {picks.map((pick) => {
            const active = isChipActive(pick);
            return (
              <button
                key={pick.subcategoryId}
                ref={active ? selectedChipRef : undefined}
                type="button"
                onClick={() => onSelect(pick)}
                aria-pressed={active}
                className={`flex-shrink-0 rounded-full text-sm px-4 py-1.5 transition-colors whitespace-nowrap ${
                  active
                    ? "bg-primary text-white"
                    : "bg-secondary text-foreground hover:bg-tertiary"
                }`}
              >
                {pick.subcategoryName}
              </button>
            );
          })}
        </div>
        {hasOverflow && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent"
          />
        )}
      </div>
    </div>
  );
}

/** Fade cue only when the rail actually overflows horizontally. */
function useChipRailOverflow(
  railRef: React.RefObject<HTMLDivElement | null>,
  onOverflowChange: (overflow: boolean) => void,
) {
  useEffect(() => {
    const el = railRef.current;
    if (!el) return;

    const measure = () => {
      onOverflowChange(el.scrollWidth > el.clientWidth + 1);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    el.addEventListener("scroll", measure, { passive: true });
    return () => {
      observer.disconnect();
      el.removeEventListener("scroll", measure);
    };
  }, [railRef, onOverflowChange]);
}

/** Keep the active chip visible after selection changes. */
function useScrollSelectedChipIntoView(
  railRef: React.RefObject<HTMLDivElement | null>,
  chipRef: React.RefObject<HTMLButtonElement | null>,
  activeKey: string | null,
) {
  useEffect(() => {
    if (!activeKey) return;
    const chip = chipRef.current;
    const rail = railRef.current;
    if (!chip || !rail) return;
    chip.scrollIntoView({
      behavior: "smooth",
      inline: "nearest",
      block: "nearest",
    });
  }, [activeKey, chipRef, railRef]);
}

export default MarketCategorySelector;
